import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { apiClient, type AuditEvent, type AuditFilters } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { formatTimestamp } from '@/lib/format';
import { cn } from '@/lib/utils';

type Mode = 'panel' | 'dialog';

type Props = {
  /** Locked filters supplied by the parent. Merged with any panel-level filters. */
  filters: AuditFilters;
  /** ``dialog`` strips the load-older + tail controls so the dialog stays compact. */
  mode?: Mode;
  /** When true the panel polls for new rows on an interval. */
  tailing?: boolean;
  /** Page size for one fetch. Defaults to the filter's limit or 100. */
  pageSize?: number;
};

const TAIL_INTERVAL_MS = 5000;
const TAIL_MAX_ROWS = 1000;

function eventTypeBadgeVariant(eventType: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (eventType.startsWith('admin.') || eventType === 'auth.login_failed') return 'destructive';
  if (eventType.startsWith('signup.')) return 'outline';
  if (eventType.startsWith('cmd.')) return 'secondary';
  return 'default';
}

function severityDotClass(severity: string): string {
  if (severity === 'critical') return 'bg-red-500';
  if (severity === 'warning') return 'bg-amber-500';
  if (severity === 'notice') return 'bg-blue-500';
  return 'bg-muted-foreground/40';
}

function OriginRows({ payload }: { payload: Record<string, unknown> }) {
  const ip = typeof payload.ip === 'string' ? payload.ip : null;
  const userAgent = typeof payload.user_agent === 'string' ? payload.user_agent : null;
  if (!ip && !userAgent) return null;
  return (
    <>
      {ip ? (
        <>
          <dt className="font-medium">IP</dt>
          <dd className="col-span-2 font-mono">{ip}</dd>
        </>
      ) : null}
      {userAgent ? (
        <>
          <dt className="font-medium">Device</dt>
          <dd className="col-span-2 text-xs break-all">{userAgent}</dd>
        </>
      ) : null}
    </>
  );
}

/**
 * Filtered audit_event table with a clickable row → JSON drawer.
 *
 * In ``panel`` mode the table owns its own pagination and (optional) tail
 * polling. In ``dialog`` mode the table simply renders the most recent page
 * of rows that match the locked filters — used inside ``UserDetailDialog``
 * to show a per-user audit trail without overlap controls.
 */
export default function AuditEventTable({
  filters,
  mode = 'panel',
  tailing = false,
  pageSize
}: Props) {
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null);
  const [latestId, setLatestId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [drawerEvent, setDrawerEvent] = useState<AuditEvent | null>(null);
  const isMounted = useRef(true);

  const effectiveFilters = useMemo<AuditFilters>(
    () => ({ limit: pageSize ?? filters.limit ?? 100, ...filters }),
    [filters, pageSize]
  );

  const loadInitial = useCallback(async () => {
    setIsLoading(true);
    try {
      const page = await apiClient.listAuditEvents(effectiveFilters);
      if (!isMounted.current) return;
      setRows(page.events);
      setNextBeforeId(page.next_before_id);
      setLatestId(page.latest_id);
    } catch (err) {
      toast.error('Failed to load audit events', { description: String(err) });
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [effectiveFilters]);

  const loadOlder = useCallback(async () => {
    if (nextBeforeId === null) return;
    setIsLoadingMore(true);
    try {
      const page = await apiClient.listAuditEvents({
        ...effectiveFilters,
        before_id: nextBeforeId
      });
      if (!isMounted.current) return;
      setRows((prev) => [...prev, ...page.events]);
      setNextBeforeId(page.next_before_id);
    } catch (err) {
      toast.error('Failed to load more events', { description: String(err) });
    } finally {
      if (isMounted.current) setIsLoadingMore(false);
    }
  }, [effectiveFilters, nextBeforeId]);

  const pollNewer = useCallback(async () => {
    if (latestId === null) return;
    try {
      const page = await apiClient.listAuditEvents({
        ...effectiveFilters,
        after_id: latestId,
        limit: 50
      });
      if (!isMounted.current || page.events.length === 0) return;
      setRows((prev) => {
        const combined = [...page.events.slice().reverse(), ...prev];
        return combined.slice(0, TAIL_MAX_ROWS);
      });
      if (page.latest_id !== null) setLatestId(page.latest_id);
    } catch (err) {
      console.warn('audit tail poll failed', err);
    }
  }, [effectiveFilters, latestId]);

  useEffect(() => {
    isMounted.current = true;
    loadInitial();
    return () => {
      isMounted.current = false;
    };
  }, [loadInitial]);

  useEffect(() => {
    if (mode !== 'panel' || !tailing) return undefined;
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !drawerEvent) {
        pollNewer();
      }
    }, TAIL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [mode, tailing, drawerEvent, pollNewer]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">No audit events match the current filters.</p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">Timestamp</TableHead>
              <TableHead className="w-48">Event</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead className="w-32">Actor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => setDrawerEvent(row)}
              >
                <TableCell className="font-mono text-xs">{formatTimestamp(row.ts)}</TableCell>
                <TableCell>
                  <span
                    className={cn('mr-2 inline-block h-2 w-2 rounded-full align-middle', severityDotClass(row.severity))}
                    title={row.severity}
                  />
                  <Badge variant={eventTypeBadgeVariant(row.eventType)}>{row.eventType}</Badge>
                </TableCell>
                <TableCell className="text-sm">{row.summary}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {row.userIdentifier ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {mode === 'panel' && nextBeforeId !== null && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={loadOlder} disabled={isLoadingMore}>
            {isLoadingMore ? 'Loading…' : 'Load older'}
          </Button>
        </div>
      )}

      <Sheet open={drawerEvent !== null} onOpenChange={(open) => !open && setDrawerEvent(null)}>
        <SheetContent className="w-[640px] sm:max-w-none overflow-y-auto">
          {drawerEvent && (
            <>
              <SheetHeader>
                <SheetTitle>{drawerEvent.eventType}</SheetTitle>
                <SheetDescription>{drawerEvent.summary}</SheetDescription>
              </SheetHeader>
              <dl className="grid grid-cols-3 gap-x-3 gap-y-2 mt-4 text-sm">
                <dt className="font-medium">ID</dt>
                <dd className="col-span-2 font-mono">{drawerEvent.id}</dd>
                <dt className="font-medium">Timestamp</dt>
                <dd className="col-span-2 font-mono">{formatTimestamp(drawerEvent.ts)}</dd>
                <dt className="font-medium">Source</dt>
                <dd className="col-span-2">{drawerEvent.source}</dd>
                <dt className="font-medium">Severity</dt>
                <dd className="col-span-2 capitalize">{drawerEvent.severity}</dd>
                <dt className="font-medium">User</dt>
                <dd className="col-span-2">{drawerEvent.userIdentifier ?? '—'}</dd>
                <OriginRows payload={drawerEvent.payload} />
                <dt className="font-medium">Operation</dt>
                <dd className="col-span-2 font-mono text-xs">{drawerEvent.operationId ?? '—'}</dd>
                <dt className="font-medium">Thread</dt>
                <dd className="col-span-2 font-mono text-xs">{drawerEvent.threadId ?? '—'}</dd>
                <dt className="font-medium">Hash</dt>
                <dd className="col-span-2 font-mono text-xs break-all">{drawerEvent.eventHash}</dd>
              </dl>
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Payload</p>
                <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(drawerEvent.payload, null, 2)}
                </pre>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
